const SVG_TAG_REGEX = /<svg\b([^>]*)>/i;

const attrRegex = (name) =>
    new RegExp(`\\b${name}=(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, 'i');

const getAttrValue = (attrs, name) => {
    const match = String(attrs || '').match(attrRegex(name));
    return match ? match[1] || match[2] || match[3] || '' : '';
};

const replaceOrAddAttr = (attrs, name, value) => {
    const pattern = new RegExp(`\\s${name}=("[^"]*"|'[^']*'|[^\\s>]+)`, 'i');
    if (pattern.test(attrs)) {
        return attrs.replace(pattern, ` ${name}="${value}"`);
    }
    return `${attrs} ${name}="${value}"`;
};

const parseNumericDimension = (value) => {
    if (!value) return null;
    const raw = String(value).trim();
    if (/^\d+(\.\d+)?$/.test(raw)) return Number(raw);
    if (/^\d+(\.\d+)?px$/i.test(raw)) return Number(raw.replace(/px$/i, ''));
    return null;
};

const parseViewBox = (value) => {
    const parts = String(value || '')
        .trim()
        .split(/[\s,]+/)
        .map(Number);
    if (parts.length !== 4 || parts.some((part) => Number.isNaN(part))) {
        return null;
    }
    const [, , width, height] = parts;
    if (width <= 0 || height <= 0) return null;
    return { width, height };
};

const DEFAULT_SVG_DIMENSION = 512;

const isSvgUploadFile = (file) => {
    if (!file || typeof file.text !== 'function') return false;
    const type = String(file.type || '').toLowerCase();
    const name = String(file.name || '').toLowerCase();
    return type === 'image/svg+xml' || name.endsWith('.svg');
};

const escapeRegExp = (value) =>
    String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const inlineSvgClassStyles = (markup) => {
    if (typeof markup !== 'string' || !/<style\b/i.test(markup)) {
        return markup;
    }

    const classStyles = new Map();
    for (const styleMatch of markup.matchAll(
        /<style\b[^>]*>([\s\S]*?)<\/style>/gi
    )) {
        const css = styleMatch[1] || '';
        for (const ruleMatch of css.matchAll(
            /\.([a-z_][\w-]*)\s*\{([^{}]+)\}/gi
        )) {
            const className = ruleMatch[1];
            const declarations = String(ruleMatch[2] || '')
                .trim()
                .replace(/;?\s*$/, ';');
            if (declarations) classStyles.set(className, declarations);
        }
    }

    let normalized = markup;
    classStyles.forEach((declarations, className) => {
        const escapedClass = escapeRegExp(className);
        const elementPattern = new RegExp(
            `<([a-z][\\w:-]*)([^>]*\\bclass=(['"])[^'"]*\\b${escapedClass}\\b[^'"]*\\3[^>]*)>`,
            'gi'
        );
        normalized = normalized.replace(
            elementPattern,
            (element, tagName, attrs) => {
                const isSelfClosing = /\/\s*$/.test(attrs);
                const cleanAttrs = attrs.replace(/\/\s*$/, '');
                const closingSlash = isSelfClosing ? ' /' : '';
                if (/\sstyle=(['"])/i.test(cleanAttrs)) {
                    return `<${tagName}${cleanAttrs.replace(
                        /\sstyle=(['"])([\s\S]*?)\1/i,
                        (_style, quote, current) =>
                            ` style=${quote}${current};${declarations}${quote}`
                    )}${closingSlash}>`;
                }
                return `<${tagName}${cleanAttrs} style="${declarations}"${closingSlash}>`;
            }
        );
    });

    return normalized.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '');
};

export const normalizeSvgMarkupForUpload = (markup) => {
    if (typeof markup !== 'string' || !SVG_TAG_REGEX.test(markup)) {
        return markup;
    }

    const normalizedRoot = markup.replace(
        SVG_TAG_REGEX,
        (match, attrs = '') => {
            let next = attrs || '';
            const xmlnsAttr = getAttrValue(next, 'xmlns');
            const widthAttr = getAttrValue(next, 'width');
            const heightAttr = getAttrValue(next, 'height');
            const viewBoxAttr = getAttrValue(next, 'viewBox');
            const widthValue = parseNumericDimension(widthAttr);
            const heightValue = parseNumericDimension(heightAttr);
            const viewBox = parseViewBox(viewBoxAttr);

            const resolvedWidth =
                widthValue || viewBox?.width || DEFAULT_SVG_DIMENSION;
            const resolvedHeight =
                heightValue || viewBox?.height || DEFAULT_SVG_DIMENSION;

            if (!xmlnsAttr) {
                next = replaceOrAddAttr(
                    next,
                    'xmlns',
                    'http://www.w3.org/2000/svg'
                );
            }

            if (!widthValue) {
                next = replaceOrAddAttr(next, 'width', `${resolvedWidth}`);
            }
            if (!heightValue) {
                next = replaceOrAddAttr(next, 'height', `${resolvedHeight}`);
            }
            if (viewBoxAttr) {
                // Correct common lowercase `viewbox` exports. Attribute names are
                // case-sensitive when an SVG is served as standalone XML.
                next = replaceOrAddAttr(next, 'viewBox', viewBoxAttr);
            } else {
                next = replaceOrAddAttr(
                    next,
                    'viewBox',
                    `0 0 ${resolvedWidth} ${resolvedHeight}`
                );
            }

            return `<svg${next}>`;
        }
    );

    return normalizedRoot;
};

export const normalizeSvgFileForUpload = async (file) => {
    if (!isSvgUploadFile(file) || typeof File !== 'function') {
        return file;
    }

    try {
        const markup = await file.text();
        const normalizedMarkup = normalizeSvgMarkupForUpload(markup);
        const hasCorrectMime =
            String(file.type || '').toLowerCase() === 'image/svg+xml';
        if (
            !normalizedMarkup ||
            (normalizedMarkup === markup && hasCorrectMime)
        ) {
            return file;
        }

        return new File([normalizedMarkup], file.name, {
            type: 'image/svg+xml',
            lastModified: file.lastModified,
        });
    } catch {
        return file;
    }
};
